import { FC, useCallback, useEffect, useRef, useState } from "react";
import { AirplaneLanding, AirplaneTakeoff, MagnifyingGlass, MapPin } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import useIntlPlaceSuggestions, { PlaceSuggestion } from "../hooks/useIntlPlaceSuggestions";

/**
 * Airport/city picker for the international-v2 search flow — queries
 * Duffel's places API live (debounced), unlike ../components/AirportSelector
 * (Travex, preloads the full list once and filters client-side). Kept as a
 * separate component so the old Travex-only search flow is untouched.
 */
const IntlAirportSelector: FC<{
  onSelect?: (place: PlaceSuggestion) => void;
  placeholder?: string;
  isDepature?: boolean;
  /** Optional — lets a parent overwrite the displayed text (e.g. an
   * origin/destination swap button) without taking over free-typing. */
  value?: string;
}> = ({ placeholder = "Enter city or airport", isDepature = true, onSelect, value }) => {
  const [searchValue, setSearchValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value !== undefined && value !== searchValue) {
      setSearchValue(value);
    }
    // Only react to external `value` changes (e.g. a swap) — intentionally
    // excludes `searchValue` so this doesn't fight the user while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const debouncedSearch = useDebouncedValue(searchValue, 300);
  const { places, isLoadingPlaces } = useIntlPlaceSuggestions(debouncedSearch);

  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(
    (place: PlaceSuggestion) => {
      onSelect?.(place);
      setSearchValue(`${place.code} - ${place.city ?? place.name ?? ""}`);
      setShowDropdown(false);
    },
    [onSelect]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isOpen = showDropdown && debouncedSearch.trim().length >= 2;

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`rounded-xl border px-4 w-full flex gap-2.5 items-center transition-all duration-200 ${
          isFocused
            ? "border-visaro-orange ring-4 ring-visaro-orange/10"
            : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <span className={`shrink-0 transition-colors duration-200 ${isFocused ? "text-visaro-orange" : "text-gray-400"}`}>
          {isDepature ? <AirplaneTakeoff size={19} weight="duotone" /> : <AirplaneLanding size={19} weight="duotone" />}
        </span>
        <input
          type="text"
          value={searchValue}
          onFocus={() => setIsFocused(true)}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setShowDropdown(true);
          }}
          placeholder={placeholder}
          className="py-3.5 focus:outline-none flex-1 bg-transparent text-deepBlue placeholder:text-gray-400"
        />
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute z-50 bg-white shadow-2xl shadow-black/10 top-full mt-2 left-0 w-full rounded-2xl border border-gray-100 max-h-96 overflow-y-auto overflow-x-hidden"
          >
            {isLoadingPlaces ? (
              <div className="flex items-center gap-2.5 px-4 py-5 text-sm text-gray-400">
                <MagnifyingGlass size={16} className="animate-pulse" />
                Searching airports…
              </div>
            ) : places.length === 0 ? (
              <div className="flex flex-col items-center gap-1.5 px-4 py-6 text-center">
                <MapPin size={22} className="text-gray-300" weight="duotone" />
                <span className="text-sm text-gray-500">No airports found</span>
                <span className="text-xs text-gray-400">Try a different city or airport code</span>
              </div>
            ) : (
              <div className="py-1.5">
                {places.map((place, index) => (
                  <button
                    key={place.code + index}
                    onClick={() => handleSelect(place)}
                    type="button"
                    className="group flex items-center gap-3 w-full px-3.5 py-2.5 hover:bg-visaro-orange/5 focus:bg-visaro-orange/5 cursor-pointer text-left transition-colors duration-150 outline-none"
                  >
                    <span className="flex items-center justify-center size-9 rounded-full bg-gray-50 text-gray-400 shrink-0 transition-colors duration-150 group-hover:bg-visaro-orange group-hover:text-white">
                      <MapPin size={16} weight="fill" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-semibold text-deepBlue truncate">{place.city ?? place.name}</span>
                        {place.country && (
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide shrink-0">
                            {place.country}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{place.name}</div>
                    </div>
                    <span className="shrink-0 text-xs font-bold text-visaro-orange bg-visaro-orange/10 px-2 py-1 rounded-md tabular-nums transition-colors duration-150 group-hover:bg-visaro-orange group-hover:text-white">
                      {place.code}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IntlAirportSelector;
